import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  Plus,
  RefreshCw,
  Send,
  Trash2,
  Wand2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { approveProposal, deleteProposal, generateProposalPdf, markProposalSent, sendProposal } from '@/lib/orcaBackend';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

type ProposalStatus = 'draft' | 'broker_review' | 'approved' | 'generated' | 'sent' | 'archived';
type CommercialUse = 'charter' | 'sale' | 'management' | 'mixed' | 'unknown';

interface ConversationOption {
  id: string;
  client_id: string;
  destination: string | null;
  summary: string | null;
  broker_id: string | null;
  clients?: {
    full_name: string;
    email?: string;
    phone?: string;
  };
}

interface BrokerageAsset {
  id: string;
  name: string;
  builder: string | null;
  model: string | null;
  length_m: number | null;
  current_location: string | null;
  commercial_use: CommercialUse;
  asset_status: string;
}

interface ProposalDocument {
  id: string;
  title: string;
  status: ProposalStatus;
  conversation_id: string | null;
  client_id: string | null;
  broker_id: string | null;
  pdf_url: string | null;
  selected_asset_count: number;
  generated_at: string | null;
  sent_at: string | null;
  updated_at: string | null;
}

interface ProposalAsset {
  id: string;
  proposal_id: string;
  brokerage_asset_id: string;
  broker_notes: string | null;
  sort_order: number;
  brokerage_assets?: BrokerageAsset | null;
}

interface ProposalControlsPanelProps {
  brokerageId: string;
  conversations: ConversationOption[];
}

// New proposal tables are ahead of the generated Supabase TS types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const proposalStatusLabel = (status: ProposalStatus) => status.replace('_', ' ');

const formatAssetLine = (asset?: BrokerageAsset | null) => {
  if (!asset) return 'Unknown asset';
  return [
    asset.name,
    asset.builder,
    asset.model,
    asset.length_m ? `${asset.length_m}m` : null,
    asset.current_location,
  ]
    .filter(Boolean)
    .join(' | ');
};

export const ProposalControlsPanel = ({ brokerageId, conversations }: ProposalControlsPanelProps) => {
  const queryClient = useQueryClient();
  const { user, session, profile } = useAuth();
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string>('');
  const [proposalTitle, setProposalTitle] = useState('Yacht proposal');
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [assetNote, setAssetNote] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const proposalsQuery = useQuery({
    queryKey: ['proposal-documents', brokerageId],
    queryFn: async () => {
      const { data, error } = await db
        .from('proposal_documents')
        .select('*')
        .eq('brokerage_id', brokerageId)
        .neq('status', 'archived')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []) as ProposalDocument[];
    },
    enabled: !!brokerageId,
  });

  const selectedProposal = useMemo(
    () => proposalsQuery.data?.find((proposal) => proposal.id === selectedProposalId) || null,
    [proposalsQuery.data, selectedProposalId]
  );

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  const activeAssetsQuery = useQuery({
    queryKey: ['proposal-active-assets', brokerageId],
    queryFn: async () => {
      const { data, error } = await db
        .from('brokerage_assets')
        .select('id,name,builder,model,length_m,current_location,commercial_use,asset_status')
        .eq('brokerage_id', brokerageId)
        .eq('asset_status', 'active')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return (data || []) as BrokerageAsset[];
    },
    enabled: !!brokerageId,
  });

  const proposalAssetsQuery = useQuery({
    queryKey: ['proposal-assets', brokerageId, selectedProposalId],
    queryFn: async () => {
      const { data, error } = await db
        .from('proposal_assets')
        .select(
          `
          *,
          brokerage_assets (
            id,
            name,
            builder,
            model,
            length_m,
            current_location,
            commercial_use,
            asset_status
          )
        `
        )
        .eq('brokerage_id', brokerageId)
        .eq('proposal_id', selectedProposalId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data || []) as ProposalAsset[];
    },
    enabled: !!brokerageId && !!selectedProposalId,
  });

  const refreshProposalData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['proposal-documents', brokerageId] }),
      queryClient.invalidateQueries({ queryKey: ['proposal-assets', brokerageId, selectedProposalId] }),
    ]);
  };

  const syncSelectedAssetCount = async (proposalId: string) => {
    const { count, error: countError } = await db
      .from('proposal_assets')
      .select('id', { count: 'exact', head: true })
      .eq('brokerage_id', brokerageId)
      .eq('proposal_id', proposalId);

    if (countError) throw countError;

    const { error: updateError } = await db
      .from('proposal_documents')
      .update({ selected_asset_count: count || 0 })
      .eq('brokerage_id', brokerageId)
      .eq('id', proposalId);

    if (updateError) throw updateError;
  };

  const selectedAssetCount = proposalAssetsQuery.data?.length ?? selectedProposal?.selected_asset_count ?? 0;
  const activeAssetCount = activeAssetsQuery.data?.length || 0;
  const selectedProposalStatus = selectedProposal?.status || null;
  const isLegacyPrePdfApproval = selectedProposalStatus === 'approved' && !selectedProposal?.pdf_url;
  const canEditShortlist =
    !!selectedProposalId &&
    !!selectedProposalStatus &&
    (['draft', 'broker_review'].includes(selectedProposalStatus) || isLegacyPrePdfApproval);
  const canGenerateProposal =
    !!selectedProposalId &&
    selectedAssetCount > 0 &&
    !!selectedProposalStatus &&
    (['draft', 'broker_review', 'generated'].includes(selectedProposalStatus) || isLegacyPrePdfApproval);
  const canApproveProposal =
    !!selectedProposalId &&
    selectedAssetCount > 0 &&
    !!selectedProposal?.pdf_url &&
    selectedProposalStatus === 'generated';
  const canMarkSent =
    !!selectedProposalId &&
    !!selectedProposal?.pdf_url &&
    selectedProposalStatus === 'approved';
  const canSendProposal = canMarkSent;

  const createProposalMutation = useMutation({
    mutationFn: async () => {
      if (!selectedConversation) throw new Error('Select a conversation first.');

      const payload = {
        brokerage_id: brokerageId,
        conversation_id: selectedConversation.id,
        client_id: selectedConversation.client_id,
        broker_id: selectedConversation.broker_id || profile?.broker_id || null,
        status: 'broker_review',
        title: proposalTitle.trim() || 'Yacht proposal',
        created_by: user?.id || null,
      };

      const { data, error } = await db.from('proposal_documents').insert(payload).select('id').single();
      if (error) throw error;
      return data as { id: string };
    },
    onSuccess: async (proposal) => {
      setFeedback('Proposal workspace created.');
      setSelectedProposalId(proposal.id);
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not create proposal.');
    },
  });

  const addAssetMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (!canEditShortlist) throw new Error('Shortlist is locked after PDF generation.');
      if (!selectedAssetId) throw new Error('Select an active asset first.');

      const payload = {
        brokerage_id: brokerageId,
        proposal_id: selectedProposalId,
        brokerage_asset_id: selectedAssetId,
        sort_order: proposalAssetsQuery.data?.length || 0,
        broker_notes: assetNote.trim() || null,
      };

      const { error } = await db.from('proposal_assets').insert(payload);
      if (error) throw error;
    },
    onSuccess: async () => {
      setFeedback('Asset added to proposal shortlist.');
      setSelectedAssetId('');
      setAssetNote('');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not add asset.');
    },
  });

  const updateProposalAssetMutation = useMutation({
    mutationFn: async ({ proposalAssetId, brokerNotes }: { proposalAssetId: string; brokerNotes: string }) => {
      if (!canEditShortlist) throw new Error('Broker notes are locked after PDF generation.');
      const { error } = await db
        .from('proposal_assets')
        .update({ broker_notes: brokerNotes.trim() || null })
        .eq('brokerage_id', brokerageId)
        .eq('id', proposalAssetId);

      if (error) throw error;
      await syncSelectedAssetCount(selectedProposalId);
    },
    onSuccess: async () => {
      setFeedback('Broker notes updated.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not update notes.');
    },
  });

  const removeProposalAssetMutation = useMutation({
    mutationFn: async (proposalAssetId: string) => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (!canEditShortlist) throw new Error('Shortlist is locked after PDF generation.');
      const { error } = await db
        .from('proposal_assets')
        .delete()
        .eq('brokerage_id', brokerageId)
        .eq('id', proposalAssetId);

      if (error) throw error;
      await syncSelectedAssetCount(selectedProposalId);
    },
    onSuccess: async () => {
      setFeedback('Asset removed from proposal.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not remove asset.');
    },
  });

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (!selectedProposal?.pdf_url) throw new Error('Generate and review the PDF before approval.');
      if (selectedProposal?.status !== 'generated') throw new Error('Only generated PDFs can be approved.');
      return approveProposal({
        proposalId: selectedProposalId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onMutate: () => {
      setFeedback('Approving generated PDF...');
    },
    onSuccess: async () => {
      setFeedback('Proposal PDF approved for manual sending.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not approve proposal.');
    },
  });

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (selectedAssetCount === 0) throw new Error('Add at least one active asset before generating a PDF.');
      if (
        !['draft', 'broker_review', 'generated'].includes(selectedProposal?.status || '') &&
        !(selectedProposal?.status === 'approved' && !selectedProposal?.pdf_url)
      ) {
        throw new Error('Only broker-review proposals can generate a PDF.');
      }
      return generateProposalPdf({
        proposalId: selectedProposalId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onMutate: () => {
      setFeedback('Generating proposal PDF...');
    },
    onSuccess: async () => {
      setFeedback('Proposal PDF generated.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not generate PDF.');
    },
  });

  const markSentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (selectedProposal?.status !== 'approved') throw new Error('Approve the generated PDF before marking sent.');
      return markProposalSent({
        proposalId: selectedProposalId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onMutate: () => {
      setFeedback('Marking proposal as manually sent...');
    },
    onSuccess: async () => {
      setFeedback('Proposal marked as manually sent.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not mark proposal sent.');
    },
  });

  const sendProposalMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      if (selectedProposal?.status !== 'approved') throw new Error('Approve the generated PDF before sending.');
      return sendProposal({
        proposalId: selectedProposalId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onMutate: () => {
      setFeedback('Sending proposal to client chat...');
    },
    onSuccess: async () => {
      setFeedback('Proposal sent to client chat.');
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not send proposal.');
    },
  });

  const deleteProposalMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProposalId) throw new Error('Select a proposal first.');
      return deleteProposal({
        proposalId: selectedProposalId,
        accessToken: session?.access_token,
        fallbackBackendUrl: profile?.ngrok_url,
      });
    },
    onMutate: () => {
      setFeedback('Deleting proposal...');
    },
    onSuccess: async () => {
      setFeedback('Proposal deleted.');
      setSelectedProposalId(null);
      await refreshProposalData();
    },
    onError: (error) => {
      setFeedback(error instanceof Error ? error.message : 'Could not delete proposal.');
    },
  });

  return (
    <section className="mt-6 rounded-lg border border-slate-200/70 bg-white/95 p-4 shadow-xl sm:mt-8 sm:p-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-700" />
            <h3 className="text-lg font-semibold text-slate-900">Proposal Controls</h3>
          </div>
          <p className="mt-1 text-sm text-slate-600">Generate PDF, approve document, then mark manual send</p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            proposalsQuery.refetch();
            activeAssetsQuery.refetch();
            if (selectedProposalId) proposalAssetsQuery.refetch();
          }}
          className="flex items-center justify-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {feedback && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {feedback}
        </div>
      )}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,440px)]">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase text-slate-600">Proposal Workspaces</h4>
            <Badge variant="outline">{proposalsQuery.data?.length || 0} proposals</Badge>
          </div>

          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="grid min-w-[560px] grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr] bg-slate-50 px-4 py-2 text-xs font-semibold uppercase text-slate-500">
              <span>Proposal</span>
              <span>Status</span>
              <span>Assets</span>
              <span>PDF</span>
            </div>
            <div className="max-h-72 overflow-auto">
              {proposalsQuery.isLoading && <div className="px-4 py-6 text-sm text-slate-500">Loading proposals...</div>}
              {proposalsQuery.error && <div className="px-4 py-6 text-sm text-red-700">Proposal data unavailable.</div>}
              {!proposalsQuery.isLoading && !proposalsQuery.error && proposalsQuery.data?.length === 0 && (
                <div className="px-4 py-6 text-sm text-slate-500">No proposal workspaces yet.</div>
              )}
              {proposalsQuery.data?.map((proposal) => (
                <button
                  key={proposal.id}
                  type="button"
                  onClick={() => setSelectedProposalId(proposal.id)}
                  className={`grid min-w-[560px] w-full grid-cols-[1.3fr_0.8fr_0.8fr_0.8fr] px-4 py-3 text-left text-sm transition hover:bg-emerald-50 ${
                    selectedProposalId === proposal.id ? 'bg-emerald-50' : 'bg-white'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-900">{proposal.title}</span>
                    <span className="block truncate text-xs text-slate-500">{proposal.id}</span>
                  </span>
                  <span className="capitalize text-slate-600">{proposalStatusLabel(proposal.status)}</span>
                  <span className="text-slate-600">{proposal.selected_asset_count || 0}</span>
                  <span className="text-slate-600">{proposal.pdf_url ? 'Ready' : 'None'}</span>
                </button>
              ))}
            </div>
          </div>

          {selectedProposal && (
            <div className="mt-4 rounded-lg border border-slate-200 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h4 className="font-semibold text-slate-900">{selectedProposal.title}</h4>
                  <p className="text-sm capitalize text-slate-600">
                    Status: {proposalStatusLabel(selectedProposal.status)}
                  </p>
                  {selectedAssetCount === 0 && (
                    <p className="mt-1 text-sm text-amber-700">Add at least one active asset before generating a PDF.</p>
                  )}
                  {selectedAssetCount > 0 && canGenerateProposal && !selectedProposal.pdf_url && (
                    <p className="mt-1 text-sm text-amber-700">Generate the PDF before approval.</p>
                  )}
                  {selectedProposal.status === 'generated' && selectedProposal.pdf_url && (
                    <p className="mt-1 text-sm text-amber-700">Review the generated PDF, then approve it for manual sending.</p>
                  )}
                  {selectedProposal.status === 'approved' && selectedProposal.pdf_url && (
                    <p className="mt-1 text-sm text-emerald-700">PDF approved. It can now be marked as manually sent.</p>
                  )}
                </div>
                <div className="grid w-full gap-2 sm:flex sm:w-auto sm:flex-wrap">
                  <Button
                    type="button"
                    onClick={() => generateMutation.mutate()}
                    disabled={!canGenerateProposal || generateMutation.isPending}
                    className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Wand2 className="h-4 w-4" />
                    Generate PDF
                  </Button>
                  {selectedProposal.pdf_url && (
                    <Button type="button" variant="outline" asChild className="flex items-center justify-center gap-2">
                      <a href={selectedProposal.pdf_url} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                        Open PDF
                      </a>
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => approveMutation.mutate()}
                    disabled={!canApproveProposal || approveMutation.isPending}
                    className="flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Approve PDF
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (window.confirm('Send this approved proposal PDF to the client chat now?')) {
                        sendProposalMutation.mutate();
                      }
                    }}
                    disabled={!canSendProposal || sendProposalMutation.isPending}
                    className="flex items-center justify-center gap-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                  >
                    <Send className="h-4 w-4" />
                    Send Proposal
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => markSentMutation.mutate()}
                    disabled={!canMarkSent || markSentMutation.isPending}
                    className="flex items-center justify-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    Mark Sent
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      if (
                        window.confirm(
                          'Delete this proposal workspace and its generated PDF if available? This cannot be undone.'
                        )
                      ) {
                        deleteProposalMutation.mutate();
                      }
                    }}
                    disabled={deleteProposalMutation.isPending}
                    className="flex items-center justify-center gap-2 border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Proposal
                  </Button>
                </div>
              </div>

              {selectedProposal.pdf_url && (
                <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">PDF Preview</span>
                    <span className="text-xs capitalize text-slate-500">{proposalStatusLabel(selectedProposal.status)}</span>
                  </div>
                  <iframe
                    title={`${selectedProposal.title} PDF preview`}
                    src={`${selectedProposal.pdf_url}#toolbar=1&navpanes=0`}
                    className="h-[55dvh] min-h-[320px] w-full bg-white lg:h-[520px]"
                  />
                </div>
              )}

              <div className="mt-4 space-y-3">
                {proposalAssetsQuery.isLoading && <p className="text-sm text-slate-500">Loading shortlist...</p>}
                {!proposalAssetsQuery.isLoading && proposalAssetsQuery.data?.length === 0 && (
                  <p className="text-sm text-slate-500">No assets selected yet.</p>
                )}
                {proposalAssetsQuery.data?.map((proposalAsset) => (
                  <div key={proposalAsset.id} className="rounded-md border border-slate-200 p-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900">
                          {formatAssetLine(proposalAsset.brokerage_assets)}
                        </p>
                        <p className="mt-1 text-xs capitalize text-slate-500">
                          {proposalAsset.brokerage_assets?.commercial_use || 'unknown'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-full border-red-200 text-red-700 hover:bg-red-50 sm:w-auto"
                        disabled={!canEditShortlist}
                        onClick={() => removeProposalAssetMutation.mutate(proposalAsset.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      className="mt-3"
                      rows={2}
                      defaultValue={proposalAsset.broker_notes || ''}
                      placeholder="Broker notes for this proposal"
                      onBlur={(event) =>
                        updateProposalAssetMutation.mutate({
                          proposalAssetId: proposalAsset.id,
                          brokerNotes: event.target.value,
                        })
                      }
                      disabled={!canEditShortlist}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-700" />
              <h4 className="font-semibold text-slate-900">Create Proposal</h4>
            </div>
            <div className="grid gap-3">
              <div>
                <Label htmlFor="proposal-conversation">Conversation</Label>
                <select
                  id="proposal-conversation"
                  value={selectedConversationId}
                  onChange={(event) => setSelectedConversationId(event.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select client conversation</option>
                  {conversations.map((conversation) => (
                    <option key={conversation.id} value={conversation.id}>
                      {conversation.clients?.full_name || conversation.clients?.phone || conversation.id}
                      {conversation.destination ? ` | ${conversation.destination}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="proposal-title">Title</Label>
                <Input
                  id="proposal-title"
                  value={proposalTitle}
                  onChange={(event) => setProposalTitle(event.target.value)}
                />
              </div>
              <Button
                type="button"
                onClick={() => createProposalMutation.mutate()}
                disabled={createProposalMutation.isPending}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4" />
                Create
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-emerald-700" />
              <h4 className="font-semibold text-slate-900">Add Shortlist Asset</h4>
            </div>
            {!selectedProposal && <p className="text-sm text-slate-500">Select a proposal first.</p>}
            {selectedProposal && !canEditShortlist && (
              <p className="text-sm text-slate-500">Shortlist is locked after PDF generation.</p>
            )}
            {selectedProposal && (
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="proposal-asset">Active Asset</Label>
                  <select
                    id="proposal-asset"
                    value={selectedAssetId}
                    onChange={(event) => setSelectedAssetId(event.target.value)}
                    disabled={!canEditShortlist}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">
                      {activeAssetCount > 0 ? 'Select active asset' : 'No active assets available'}
                    </option>
                    {activeAssetsQuery.data?.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {formatAssetLine(asset)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="proposal-asset-note">Broker note</Label>
                  <Textarea
                    id="proposal-asset-note"
                    value={assetNote}
                    onChange={(event) => setAssetNote(event.target.value)}
                    disabled={!canEditShortlist}
                    rows={3}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => addAssetMutation.mutate()}
                  disabled={!canEditShortlist || addAssetMutation.isPending || activeAssetCount === 0}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add to Proposal
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
